/*
  One behaviour for every sign-up on the site.

  Shopify's own {% form 'customer' %} posts the whole page: the browser leaves,
  comes back on a fresh load and lands wherever that load lands — on the
  homepage, the top, with the confirmation forty thousand pixels below in the
  footer. So the form is sent in the background instead, and the confirmation
  takes the form's place, where the person is already looking.

  Two halves of a contract:

  - The form carries data-signup and holds three parts: [data-signup-fields],
    which is what gets swapped out; [data-signup-success], hidden until it is
    earned; and [data-signup-error], empty until something is wrong.
  - The Liquid always draws both the fields and the confirmation, and lets
    form.posted_successfully? decide which one starts hidden, marking the form
    with [data-signup-posted] when it came back confirmed. That is what answers
    when this script cannot: a visitor with no JavaScript, and the moment
    Shopify stops to ask whether someone is a robot.

  Nothing here writes copy. Every word it shows is already in the page, put
  there in Liquid, so the theme editor stays in charge of the wording.
*/
(function () {
  /*
    Shopify answers a sign-up with a redirect back to the page it came from.
    Which redirect it is, is the whole answer: the address carries this when the
    contact was taken, and carries it never when the form came back with
    something to fix.
  */
  var ACCEPTED = 'customer_posted=true';

  /* Which form was sent to Shopify's robot check page, kept across the trip
     there and back: the address it returns to does not say. */
  var SENT_KEY = 'hogo-signup-sent';

  /* How long a sign-up may wait on the spam check before its button is handed
     back — hCaptcha may still be loading on a slow line, or never arrive
     where it is blocked, or the visitor may have closed a puzzle it put up. */
  var CHECK_PATIENCE = 20000;

  /* Set when the spam check takes over a form. */
  var BOUND = 'data-hcaptcha-bound';

  /* How long the form takes to settle into the height of its confirmation. */
  var GLIDE = 300;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function parts(form) {
    return {
      fields: form.querySelector('[data-signup-fields]'),
      success: form.querySelector('[data-signup-success]'),
      error: form.querySelector('[data-signup-error]'),
      button: form.querySelector('[type="submit"]')
    };
  }

  /*
    The colour wash, the photo drift and the highlight measure the page only
    as it scrolls. When a form changes height under a page that is standing
    still, they are told the same way scrolling would tell them.
  */
  function remeasure() {
    window.dispatchEvent(new Event('scroll'));
  }

  function sending(form, on) {
    var button = parts(form).button;
    if (!button) return;

    if (on) {
      /* Already sending: waiting on the spam check came first. */
      if (button.hasAttribute('data-signup-label')) {
        button.disabled = true;
        return;
      }
      /* Held at its width, so 'SENDING…' does not stretch or shrink the row
         the button sits in. */
      button.style.minWidth = button.getBoundingClientRect().width + 'px';
      var label = button.getAttribute('data-signup-sending');
      if (label) {
        button.setAttribute('data-signup-label', button.innerHTML);
        button.innerHTML = label;
      }
      button.disabled = true;
      return;
    }

    var was = button.getAttribute('data-signup-label');
    if (was !== null) {
      button.innerHTML = was;
      button.removeAttribute('data-signup-label');
    }
    button.style.minWidth = '';
    button.disabled = false;
  }

  function fail(form, message) {
    var p = parts(form);
    sending(form, false);
    if (!p.error) return;

    /* The form writes its own sentence for the times we only know that it did
       not work — a connection that dropped, an answer we cannot read. */
    p.error.textContent = message || p.error.getAttribute('data-signup-error-text') || '';
    p.error.hidden = false;
  }

  function stopWaiting(form) {
    clearTimeout(form.hogoCheckWait);
    if (form.hogoCheckWatch) {
      form.hogoCheckWatch.disconnect();
      form.hogoCheckWatch = null;
    }
  }

  /*
    Waiting on Shopify's spam check, which sends the form here once it has its
    answer. Until then the button says so. If no answer comes — hCaptcha never
    loaded, failed, or the visitor closed its puzzle, none of which tells this
    script — the button is handed back with the form's sentence for a spam
    check that did not get through.
  */
  function waitForCheck(form) {
    sending(form, true);
    clearTimeout(form.hogoCheckWait);
    form.hogoCheckWait = setTimeout(function () {
      stopWaiting(form);
      var error = parts(form).error;
      fail(form, error ? error.getAttribute('data-signup-check-error-text') : '');
    }, CHECK_PATIENCE);
  }

  /* Shopify's spam check is on for this store's sign-ups. */
  function checked() {
    return !!(window.Shopify && window.Shopify.captcha);
  }

  /*
    Sends the form through the spam check from outside a submit. Once the check
    has the form, its form.submit asks hCaptcha for an answer and hands the
    form, answer attached, to send(). Until then — the check only takes over a
    form once hCaptcha has loaded, two seconds on a good line and never where
    hCaptcha is blocked — the form waits for it rather than going out without
    an answer, which Shopify turns away ('Missing CAPTCHA token').
  */
  function sendThroughCheck(form) {
    waitForCheck(form);

    if (form.hasAttribute(BOUND)) {
      form.submit();
      return;
    }

    if (form.hogoCheckWatch || typeof MutationObserver !== 'function') return;
    form.hogoCheckWatch = new MutationObserver(function () {
      if (!form.hasAttribute(BOUND)) return;
      form.hogoCheckWatch.disconnect();
      form.hogoCheckWatch = null;
      form.submit();
    });
    form.hogoCheckWatch.observe(form, { attributes: true, attributeFilter: [BOUND] });

    /*
      The check sets itself up on the first field focused; a form sent
      without one, filled in by the browser, is set up now. It is told the
      way a focused field would tell it, not through Shopify.captcha.protect:
      only that way does the check give the form the key Shopify needs to
      take it to its robot check page, and a form without it that Shopify
      wants to check is turned away ('invalid parameters').
    */
    if (!form.hasAttribute('data-cptcha')) {
      var field = form.querySelector('input:not([type="hidden"])');
      if (field) field.dispatchEvent(new Event('focusin', { bubbles: true }));
    }
  }

  function showConfirmation(form) {
    var p = parts(form);
    /* Hidden rather than removed: the fields keep whatever was typed, in case
       anything ever needs to hand them back. */
    if (p.fields) p.fields.hidden = true;
    if (p.success) p.success.hidden = false;
  }

  function showFields(form) {
    var p = parts(form);
    if (p.fields) p.fields.hidden = false;
    if (p.success) p.success.hidden = true;
  }

  /*
    The confirmation is shorter than the fields it replaces — on a phone the
    reminder loses more than a hundred pixels. Swapped outright, everything
    below jumps up at once, and at the foot of the page the whole page jumps
    with it. So the form keeps its height for a moment and glides down to the
    confirmation's, while the confirmation fades in.
  */
  function glide(form, from) {
    var success = parts(form).success;
    var to = form.getBoundingClientRect().height;

    if (reduced.matches || Math.abs(from - to) < 1) {
      remeasure();
      return;
    }

    form.style.height = from + 'px';
    form.style.overflow = 'hidden';
    if (success) success.style.opacity = '0';

    /* Commits the starting height, so the change below is animated from it
       rather than skipped. */
    form.getBoundingClientRect();

    form.style.transition = 'height ' + GLIDE + 'ms ease';
    form.style.height = to + 'px';
    if (success) {
      success.style.transition = 'opacity ' + GLIDE + 'ms ease';
      success.style.opacity = '1';
    }

    var start = null;
    function frame(now) {
      if (start === null) start = now;
      remeasure();
      if (now - start < GLIDE + 50) {
        requestAnimationFrame(frame);
        return;
      }
      form.style.height = '';
      form.style.overflow = '';
      form.style.transition = '';
      if (success) {
        success.style.opacity = '';
        success.style.transition = '';
      }
      remeasure();
    }
    requestAnimationFrame(frame);
  }

  function succeed(form) {
    var p = parts(form);
    var from = form.getBoundingClientRect().height;

    if (p.error) {
      p.error.hidden = true;
      p.error.textContent = '';
    }
    showConfirmation(form);
    glide(form, from);

    /* Says a sign-up went through, for anything that needs to know — a
       drawer that should stay open on its confirmation. Nothing on the site
       listens for it yet. */
    form.dispatchEvent(new CustomEvent('hogo:signup', { bubbles: true }));
  }

  /*
    Shopify does not answer in JSON, it answers with the page. So the page it
    sends back is read the same way a person would read it: find this form in
    it, and see whether it came back carrying an error.
  */
  function readAnswer(form, url, html) {
    var answer;

    try {
      answer = new DOMParser().parseFromString(html, 'text/html');
    } catch (e) {
      answer = null;
    }

    if (answer) {
      var again = form.id ? answer.getElementById(form.id) : null;
      var problem = (again || answer).querySelector('[data-signup-server-error]');
      var said = problem ? problem.textContent.trim() : '';
      if (said) return { ok: false, message: said };
    }

    if (url && url.indexOf(ACCEPTED) > -1) return { ok: true };
    return { ok: false, message: '' };
  }

  /* Shopify's robot check page, and not any address that merely contains the
     word. */
  function isChallenge(url) {
    if (!url) return false;
    var path = '';
    try {
      path = new URL(url, window.location.href).pathname;
    } catch (e) {
      return false;
    }
    return /\/challenge\/?$/.test(path);
  }

  function remember(form) {
    try {
      window.sessionStorage.setItem(SENT_KEY, form.id || '');
    } catch (e) {
      /* Private windows can refuse storage; the page then shows every form
         confirmed, as Shopify draws it. */
    }
  }

  function send(form) {
    stopWaiting(form);
    var body = new FormData(form);

    /*
      On a phone the keyboard is still up from the last field. Put away now,
      while the form still stands at its full height, it closes on a page that
      is not moving — rather than in the middle of the swap, which would make
      two jumps out of one.
    */
    var focused = document.activeElement;
    if (focused && form.contains(focused) && typeof focused.blur === 'function') {
      focused.blur();
    }

    sending(form, true);

    fetch(form.getAttribute('action') || '/contact', {
      method: 'POST',
      body: body,
      headers: { Accept: 'text/html' }
    })
      .then(function (response) {
        /*
          Shopify stops some sign-ups to ask whether the visitor is a robot, and
          that question cannot be answered from in here. The visitor is taken
          to the page Shopify answered with: it holds the puzzle, fills the
          form back in from what the spam check saved, and sends them back here
          once answered. Posting the form again instead would only spend the
          check's answer a second time, which Shopify turns away ('Invalid
          CAPTCHA token').
        */
        if (isChallenge(response.url)) {
          remember(form);
          window.location.assign(response.url);
          return null;
        }

        return response.text().then(function (html) {
          /*
            Sent without the spam check's answer, or with one that ran out.
            Once, the form goes back through the check, which sends it here
            again with a fresh answer attached.
          */
          if (response.status === 400 && /captcha/i.test(html) && checked()) {
            if (!form.hogoCheckRetried) {
              form.hogoCheckRetried = true;
              sendThroughCheck(form);
              return null;
            }
            /* Turned away twice: the spam check is not getting through. */
            form.hogoCheckRetried = false;
            var error = parts(form).error;
            return { ok: false, message: error ? error.getAttribute('data-signup-check-error-text') : '' };
          }
          form.hogoCheckRetried = false;
          return readAnswer(form, response.url, html);
        });
      })
      .then(function (answer) {
        if (!answer) return;
        if (answer.ok) succeed(form);
        else fail(form, answer.message);
      })
      .catch(function () {
        form.hogoCheckRetried = false;
        fail(form, '');
      });
  }

  /*
    After a sign-up that went the old way — through Shopify's robot check page,
    or with no JavaScript — Shopify draws every sign-up on the page as
    confirmed: it only knows that a sign-up was taken, not which. A plain post
    comes back to an address ending in the form's id (#get-a-reminder-form);
    one sent to the robot check comes back without it, and remember() kept the
    form's id instead. Only that form keeps its confirmation; the others get
    their fields back. When neither says which, all stay confirmed, as before.
  */
  function sortOutPosted() {
    var marked = document.querySelectorAll('form[data-signup] [data-signup-posted]');
    if (!marked.length) return;

    var stored = '';
    try {
      stored = window.sessionStorage.getItem(SENT_KEY) || '';
      window.sessionStorage.removeItem(SENT_KEY);
    } catch (e) {
      stored = '';
    }

    var fromAddress = '';
    try {
      fromAddress = window.location.hash ? decodeURIComponent(window.location.hash.slice(1)) : '';
    } catch (e) {
      /* A malformed address names no form; the stored id still can. */
      fromAddress = '';
    }
    var sent = '';
    if (fromAddress && document.getElementById(fromAddress)) sent = fromAddress;
    else if (stored && document.getElementById(stored)) sent = stored;
    if (!sent) return;

    for (var i = 0; i < marked.length; i++) {
      var form = marked[i].closest('form[data-signup]');
      marked[i].parentNode.removeChild(marked[i]);
      if (form && form.id !== sent) showFields(form);
    }

    /*
      Back from the robot check, the page opens at its top (or wherever its
      address points), and the confirmation may be far below — the footer's is
      at the very end. The page is taken to it, as an address ending in its id
      would have.
    */
    if (sent !== fromAddress) {
      var confirmed = document.getElementById(sent);
      if (confirmed && typeof confirmed.scrollIntoView === 'function') {
        var reach = function () {
          /* The site's own scroll when it has started — the smooth-scroll
             script loads after this one — else the browser's. */
          if (window.hogoScrollTo) {
            window.hogoScrollTo(confirmed, { block: 'center', behavior: 'auto' });
          } else {
            confirmed.scrollIntoView({ block: 'center' });
          }
        };
        reach();
        /* Photos above it are still loading and push it down as they land,
           so it is reached again once they have — unless the visitor has
           started scrolling meanwhile, who is not pulled back. */
        if (document.readyState !== 'complete') {
          var reachedAt = window.pageYOffset;
          window.addEventListener('load', function () {
            if (Math.abs(window.pageYOffset - reachedAt) < 2) reach();
          }, { once: true });
        }
      }
    }
  }

  function watch(form) {
    if (form.hogoSignup) return;
    form.hogoSignup = true;

    /*
      Shopify asks hCaptcha whether every sign-up comes from a person, and a
      sign-up sent without its answer is turned away ('Missing CAPTCHA
      token'). The check takes over the form the first time a field is
      focused: from then on it catches the submit itself, gets its answer and
      only then sends the form by calling the form.submit it found when it
      took over. Putting this script's sending there first, the check ends by
      handing the form, answer attached, to send() instead of posting the page.
      Should Shopify ever stop calling it, the check posts the page the old
      way: a reload, never an error.
    */
    try {
      form.submit = function () {
        send(form);
      };
    } catch (e) {
      /* Left to the browser: the page is posted the old way. */
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var button = parts(form).button;
      if (button && button.disabled) return;

      /* A fresh try from the visitor gets its own retry. */
      form.hogoCheckRetried = false;

      /* The check has this form: it sends it once it has its answer. */
      if (form.hasAttribute(BOUND)) {
        waitForCheck(form);
        return;
      }

      /* The check is on but has not got this form yet. */
      if (checked()) {
        sendThroughCheck(form);
        return;
      }

      send(form);
    });
  }

  function collect() {
    var forms = document.querySelectorAll('form[data-signup]');
    for (var i = 0; i < forms.length; i++) watch(forms[i]);
  }

  sortOutPosted();
  collect();
  document.addEventListener('DOMContentLoaded', function () {
    sortOutPosted();
    collect();
  });
  /* The theme editor rebuilds a section by replacing its markup outright, so
     the form that comes back is a different element and needs watching afresh. */
  document.addEventListener('shopify:section:load', collect);

  /*
    A visitor sent to Shopify's robot check who changes their mind and goes
    Back gets the page as it was left, the button still saying 'Sending…'.
    Brought back from the browser's memory like that, every button is handed
    back.
  */
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    var forms = document.querySelectorAll('form[data-signup]');
    for (var i = 0; i < forms.length; i++) {
      stopWaiting(forms[i]);
      forms[i].hogoCheckRetried = false;
      sending(forms[i], false);
    }
  });
})();
